import { motion, AnimatePresence } from 'framer-motion';

import { Gradients } from '@/shared/components/theme/gradient';
import { CardDescription, CardTitle } from '@/shared/components/ui/card';
import RoundedIcon from '@/shared/components/ui/rounded-icon';

type ItemProps = {
  id: any;
  title: string;
  icon: React.ReactNode;
  subtitle?: string;
};

type ItemHighligherProps = {
  items: ItemProps[];
  selectedItem: ItemProps;
  disabled?: boolean;
  onChange?: (item: ItemProps) => void;
};

const animations = {
  fadeScale: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.2 },
  },
  fadeSlide: {
    initial: { opacity: 0, x: -10 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.2 },
  },
} as const;

const ItemHighlighter = ({
  items,
  selectedItem,
  disabled,
  onChange,
}: ItemHighligherProps) => {
  const unselectedItems = items.filter((item) => item.id !== selectedItem.id);

  const handleItemClick = (item: ItemProps) => {
    if (disabled || !onChange) return;
    onChange(item);
  };

  return (
    <div
      className={`flex w-max flex-row items-center ${items.length > 1 ? 'gap-[14px]' : ''} rounded-full border border-white/30 p-2`}
    >
      <div className="flex flex-row gap-3">
        <AnimatePresence mode="popLayout">
          {unselectedItems.map((item) => (
            <motion.div key={item.title} {...animations.fadeScale}>
              <RoundedIcon
                className={`bg-white/30 transition-all duration-200 ${!disabled && 'cursor-pointer hover:bg-white/40'}`}
                icon={item.icon}
                onClick={() => handleItemClick(item)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      <motion.div
        key={selectedItem.title}
        {...animations.fadeSlide}
        className="flex flex-row gap-3"
      >
        <RoundedIcon
          className={`${Gradients.purpleBlueGradient} transition-all duration-200`}
          icon={selectedItem.icon}
        />
        <div className="flex flex-col justify-center pr-3">
          <CardTitle>{selectedItem.title}</CardTitle>
          {selectedItem.subtitle && (
            <CardDescription>{selectedItem.subtitle}</CardDescription>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ItemHighlighter;
